// FieldRow + FieldSheet — the mobile render of every inline-edited value
// (mobile overhaul Phase 0 §0.3.3; mockup §4.2 E/F). Detail rails become a
// list of `LABEL · value · ›` rows; tapping one opens a half sheet with a
// single 16px input, Cancel left and Save accent-right. Money gets the 22px
// mono field with the live "+ GST" helper the mockup draws; email / tel /
// url / number bring up the right keyboard.
//
// FieldLabelContext lets an Inline* editor inherit the rail label its
// RailField already renders, so a lead's "Company" row opens a sheet titled
// "Company" without every call site repeating itself.

import {
  createContext,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Sheet, SheetButton } from './Sheet';
import { formatMoney, parseMoney } from '../lib/moneyFormat';

/** The label of the RailField an Inline* editor is rendered inside (mobile
    only — RailField provides it, the editors read it for their sheet title). */
export const FieldLabelContext = createContext<{ label: string } | null>(null);

export type FieldKind = 'text' | 'money' | 'email' | 'tel' | 'url' | 'number' | 'textarea';

/** One rail row on mobile: mono label left, value right, chevron when it
    opens something (mockup `.fl .f`). */
export function FieldRow({
  label,
  value,
  tone,
  empty,
  onPress,
  mono,
}: {
  label: string;
  value: ReactNode;
  tone?: 'acc' | 'gold' | 'danger';
  /** Nothing set — the value shows a faint "Add". */
  empty?: boolean;
  onPress?: () => void;
  mono?: boolean;
}) {
  const cls = `fieldrow${onPress ? ' tappable' : ''}`;
  const body = (
    <>
      <span className="fieldrow-k">{label}</span>
      <span className={`fieldrow-v${mono ? ' mono' : ''}${tone ? ` ${tone}` : ''}${empty ? ' empty' : ''}`}>
        {empty ? 'Add' : value}
      </span>
      {onPress && <span className="chev">›</span>}
    </>
  );
  if (!onPress) return <div className={cls}>{body}</div>;
  return (
    <button type="button" className={cls} onClick={onPress}>
      {body}
    </button>
  );
}

/** Keyboard + input chrome per kind. */
type InputMode = HTMLAttributes<HTMLInputElement>['inputMode'];
const INPUT_MODE: Record<FieldKind, InputMode> = {
  text: undefined,
  money: 'decimal',
  email: 'email',
  tel: 'tel',
  url: 'url',
  number: 'numeric',
  textarea: undefined,
};
const INPUT_TYPE: Record<FieldKind, string> = {
  text: 'text',
  money: 'text',
  email: 'email',
  tel: 'tel',
  url: 'url',
  number: 'text',
  textarea: 'text',
};

export function FieldSheet({
  open,
  title,
  context,
  kind,
  value,
  onSave,
  onClose,
  placeholder,
}: {
  open: boolean;
  title: string;
  /** Mono eyebrow under the title — "ex GST". */
  context?: string;
  kind: FieldKind;
  value: string;
  /** Called with the raw input text; the caller parses exactly as its
      desktop inline commit does. */
  onSave: (raw: string) => void;
  onClose: () => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const openRef = useRef(open);

  // Re-seed on each opening — the sheet stays mounted between edits.
  useEffect(() => {
    if (open && !openRef.current) setDraft(value);
    openRef.current = open;
  }, [open, value]);

  if (!open) return null;

  const save = () => {
    onSave(draft);
    onClose();
  };

  const gstCents = kind === 'money' ? parseMoney(draft) : null;

  return (
    <Sheet
      open
      onClose={onClose}
      variant="half"
      title={title}
      context={context}
      leftAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
      rightAction={
        <SheetButton tone="accent" onClick={save}>
          Save
        </SheetButton>
      }
      className="sheet-field"
    >
      <div className="fs-body">
        {kind === 'textarea' ? (
          <textarea
            className="fs-input fs-area"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
          />
        ) : kind === 'money' ? (
          <div className="fs-money">
            <span className="fs-prefix">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={draft}
              placeholder={placeholder ?? '0'}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
            />
            <span className="fs-gst">{gstCents === null ? '' : `+ GST ${formatMoney(Math.round(gstCents / 10))}`}</span>
          </div>
        ) : (
          <input
            className="fs-input"
            type={INPUT_TYPE[kind]}
            inputMode={INPUT_MODE[kind]}
            value={draft}
            placeholder={placeholder}
            autoComplete="off"
            autoCapitalize={kind === 'email' || kind === 'url' ? 'none' : undefined}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
        )}
      </div>
    </Sheet>
  );
}

/** Best guess at the keyboard a free-text value wants — an email address
    should not open the plain alphabetic keyboard just because the call site
    never said so. */
export function guessFieldKind(value: string | null | undefined): FieldKind {
  const v = (value ?? '').trim();
  if (v === '') return 'text';
  if (v.includes('@') && !v.includes(' ')) return 'email';
  if (/^(https?:\/\/|www\.)/i.test(v)) return 'url';
  if (/^[+()\d][\d\s()+-]{5,}$/.test(v)) return 'tel';
  return 'text';
}
